import { MatchaMetaLink } from '@/components/MatchaMetaLink';

import { matchaFees } from '@/constants/matchaFees';

import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { type Fees } from '@/utils/0x.types';
import { isRwaIssuer, type TokenResult } from '@/utils/0x/token-registry.types';
import { parseFees, type TradeFlow } from '@/utils/quote-fees';

interface Props {
  fees?: Fees;
  flow: TradeFlow;
  sellToken: TokenResult;
  buyToken: TokenResult;
  sellAmount: string | undefined;
  buyAmount: string | undefined;
  swapFeeBps: number | undefined;
  integratorName: string | undefined;
  isStablecoinPair: boolean;
}

export const FeeBreakdown = ({
  fees,
  flow,
  sellToken,
  buyToken,
  sellAmount,
  buyAmount,
  swapFeeBps,
  integratorName,
  isStablecoinPair,
}: Props) => {
  const isMatchaAuto = flow === 'gasless' || flow === 'intents';
  const suppressIntegratorFee =
    isMatchaAuto && (isRwaIssuer(sellToken.issuer) || isRwaIssuer(buyToken.issuer));

  if (isMatchaAuto && !fees) return null;

  const items = parseFees({
    fees,
    flow,
    sellToken,
    buyToken,
    sellAmount,
    buyAmount,
    swapFeeBps: suppressIntegratorFee ? undefined : swapFeeBps,
    integratorName: suppressIntegratorFee ? undefined : integratorName,
    fallbackMatchaAutoFeePercent: isStablecoinPair
      ? matchaFees.auto.stables / 100
      : matchaFees.auto.default / 100,
  });

  return (
    <div className="flex flex-col gap-y-2.5">
      {items.map((item) => (
        <div
          key={item.kind}
          className="text-token-gray-1600 flex items-center justify-between w-full"
        >
          <div
            className={fontStyles({
              variant: 'BODY_SM',
              class: 'text-token-gray-900 flex items-center',
            })}
          >
            {item.label}
          </div>
          {item.formattedAmount && item.token && (
            <div
              className={fontStyles({
                variant: 'BODY_SM',
                class: 'flex items-center text-right pl-2',
              })}
            >
              <TokenAvatar
                address={item.token.address}
                chainId={item.token.chainId}
                size={16}
                alt={`${item.token.name} logo`}
                showChainLogo={false}
              />
              <span className="ml-1.5">{item.formattedAmount}</span>
            </div>
          )}
        </div>
      ))}
      <div
        className={fontStyles({
          variant: 'BODY_SM',
          class:
            'flex flex-col gap-1 border-t border-special-border-divider pt-2.5 text-token-gray-800 empty:hidden',
        })}
      >
        <MatchaMetaLink />
      </div>
    </div>
  );
};
